import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTemaClaroPublico } from '../lib/theme';

/**
 * LA PÁGINA QUE ABRE EL HOMEOWNER — /p/update/<token>, sin login, fuera del AppShell.
 *
 * Es una carta, no el app: la marca, su nombre, su dirección y el update de la semana; debajo,
 * los anteriores plegados; al pie, su account manager y el teléfono. Siempre en claro. Los textos
 * de la página van en el idioma del homeowner (lo dice el backend), no en el del staff, así que
 * no pasan por el diccionario del app.
 *
 * Un token inválido, vencido o revocado muestran el MISMO mensaje, a propósito.
 */
type Vista = {
  company: string; first_name: string; address: string; claim_number: string; claim_number_kind?: 'insurance' | 'proactive' | null; account_manager: string; public_adjuster: string | null; office_phone: string;
  language: 'en' | 'es';
  updates: Array<{ id: string; sent_at: string; period_start: string; period_end: string; text: string }>;
};

const TXT = {
  en: {
    title: 'Your weekly claim update', hello: (n: string) => (n ? `Hi ${n},` : 'Hello,'), sent: 'Sent', previous: 'Previous updates', none: 'Your first update is on its way.',
    contact: 'Questions? Reply to the email or text, or call us at', manager: 'Assistant · account manager', pa: 'Your public adjuster', gone: 'This link is no longer available.',
    goneBody: 'If you need an update on your claim, call us and we\'ll be glad to help.', loading: 'Loading…', claim: 'Claim', file: 'Proactive file #',
  },
  es: {
    title: 'La actualización semanal de su reclamo', hello: (n: string) => (n ? `Hola ${n},` : 'Hola,'), sent: 'Enviado', previous: 'Actualizaciones anteriores', none: 'Su primera actualización viene en camino.',
    contact: '¿Preguntas? Responda al correo o al mensaje de texto, o llámenos al', manager: 'Asistente · account manager', pa: 'Su public adjuster', gone: 'Este enlace ya no está disponible.',
    goneBody: 'Si necesita una actualización de su reclamo, llámenos y con gusto le ayudamos.', loading: 'Cargando…', claim: 'Reclamo', file: 'N.º de expediente Proactive',
  },
} as const;

const fecha = (s: string, lang: 'en' | 'es') => new Date(s).toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' });

function Parrafos({ text }: { text: string }) {
  return <>{text.split(/\n{2,}/).map((p, i) => <p key={i} style={{ margin: '0 0 14px', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{p}</p>)}</>;
}

export function PublicHoUpdate() {
  useTemaClaroPublico();
  const { token } = useParams();
  const [sp] = useSearchParams();
  const [v, setV] = useState<Vista | null>(null);
  const [muerto, setMuerto] = useState(false);

  useEffect(() => {
    // fetch directo y no el cliente del app: acá no hay sesión ni la tiene que haber.
    const q = new URLSearchParams();
    const u = sp.get('u'); const src = sp.get('src');
    if (u) q.set('u', u); if (src) q.set('src', src);
    fetch(`/api/public/update/${token}${q.toString() ? `?${q}` : ''}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('no'))))
      .then(setV)
      .catch(() => setMuerto(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const lang: 'en' | 'es' = v?.language ?? (navigator.language.toLowerCase().startsWith('es') ? 'es' : 'en');
  const tx = TXT[lang];
  const marco = { minHeight: '100vh', background: '#f4f5f7', padding: '28px 14px', fontFamily: 'var(--font-sans)', color: '#1f2430' } as const;
  const tarjeta = { maxWidth: 620, margin: '0 auto', background: '#fff', border: '1px solid #e4e6ee', borderRadius: 12, overflow: 'hidden' } as const;
  const cabecera = (
    <div style={{ background: '#151a2d', padding: '18px 28px' }}>
      <div style={{ color: '#fff', fontSize: 17, fontWeight: 700, letterSpacing: 2 }}>PROACTIVE</div>
      <div style={{ color: '#9aa3c0', fontSize: 10, letterSpacing: 3 }}>PUBLIC ADJUSTING GROUP</div>
    </div>
  );

  if (muerto) {
    return (
      <div style={marco}><div style={tarjeta}>{cabecera}<div style={{ height: 3, background: '#5b5bd6' }} />
        <div style={{ padding: 28 }}><div style={{ fontSize: 19, fontWeight: 700, marginBottom: 6 }}>{tx.gone}</div><div style={{ color: '#6b7180', fontSize: 14 }}>{tx.goneBody}</div></div>
      </div></div>
    );
  }
  if (!v) return <div style={{ ...marco, display: 'grid', placeItems: 'center' }}><span style={{ color: '#6b7180' }}>{tx.loading}</span></div>;
  const [ultimo, ...resto] = v.updates;

  return (
    <div style={marco}>
      <div style={tarjeta}>
        {cabecera}
        <div style={{ height: 3, background: '#5b5bd6' }} />
        <div style={{ padding: '26px 28px', fontSize: 15 }}>
          <div style={{ fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: '#6b7180', fontWeight: 600 }}>{tx.title}</div>
          <div style={{ fontSize: 13, color: '#6b7180', marginTop: 4 }}>{v.address}{v.claim_number ? ` · ${v.claim_number_kind === 'proactive' ? tx.file : tx.claim} ${v.claim_number}` : ''}</div>
          {ultimo ? (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 12, color: '#6b7180', marginBottom: 12 }}>{tx.sent} {fecha(ultimo.sent_at, lang)}</div>
              <Parrafos text={ultimo.text} />
            </div>
          ) : (
            <div style={{ marginTop: 20, color: '#6b7180' }}>{tx.hello(v.first_name)} {tx.none}</div>
          )}
          {resto.length > 0 && (
            <div style={{ marginTop: 22, borderTop: '1px solid #e4e6ee', paddingTop: 14 }}>
              <div style={{ fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: '#6b7180', fontWeight: 600, marginBottom: 6 }}>{tx.previous}</div>
              {resto.map((u) => (
                <details key={u.id} style={{ marginBottom: 6 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 14, color: '#1f2430' }}>{fecha(u.sent_at, lang)}</summary>
                  <div style={{ padding: '10px 0 4px', fontSize: 14 }}><Parrafos text={u.text} /></div>
                </details>
              ))}
            </div>
          )}
        </div>
        <div style={{ background: '#fafbfe', borderTop: '1px solid #e4e6ee', padding: '14px 28px', fontSize: 12, color: '#6b7180', lineHeight: 1.6 }}>
          {v.public_adjuster && <div><b style={{ color: '#1f2430' }}>{tx.pa}:</b> {v.public_adjuster}</div>}
          {v.account_manager && <div><b style={{ color: '#1f2430' }}>{tx.manager}:</b> {v.account_manager}</div>}
          <div>{tx.contact} <b style={{ color: '#1f2430' }}>{v.office_phone}</b>.</div>
          <div style={{ marginTop: 4 }}>{v.company} · <a href="https://www.claimsproactive.com" style={{ color: '#5b5bd6', textDecoration: 'none' }}>www.claimsproactive.com</a></div>
        </div>
      </div>
    </div>
  );
}
